import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  ViewChild,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { THEME_TOKEN, Theme } from '../types/theme.types';
import { componentStylesByTheme } from '../utils/theme.utils';
import { scdPickerStylesSAG, scdPickerStylesSHS } from './scd-picker.styles';

export type ScdPickerBaseValue = {
  gid: string;
  firstName: string;
  lastName: string;
};

@Component({
  selector: 'sag-ui-scd-picker',
  templateUrl: './scd-picker.component.html',
  styleUrls: [],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ScdPickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ScdPickerComponent),
      multi: true,
    },
  ],
})
export class ScdPickerComponent<T extends ScdPickerBaseValue>
  implements ControlValueAccessor, Validator
{
  @Input() id: string = this.randomId();
  /**
   * The placeholder of the ScdPicker.
   */
  @Input()
  placeholder = '';

  /*
   * Whether the ScdPicker is required.
   */
  @Input()
  required = false;

  /**
   * Whether the ScdPicker is disabled.
   */
  @Input()
  disabled = false;

  /**
   * Whether the ScdPicker is read-only.
   */
  @Input()
  readOnly = false;

  /**
   * The label of the ScdPicker.
   */
  @Input()
  label = '';

  /**
   * Whether the SCD Picker is in an error state.
   */
  @Input() invalid = false;

  /**
   * The error message to display.
   */
  @Input() error = '';

  /**
   * Whether the switch is checked.
   */
  @Input()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: ScdPickerBaseValue | undefined;

  @Input({ required: true })
  searchFn!: (search: string) => Promise<T[]>;

  @Input() hint?: string;

  @ViewChild('input') input!: ElementRef;

  @HostListener('document:mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    const clickedInside = this.el.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.isOpen = false;
    }
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent): void {
    const focusedInside = this.el.nativeElement.contains(event.relatedTarget);
    if (!focusedInside) {
      this.isOpen = false;
    }
  }

  touched = false;
  protected selected: T | undefined;
  protected isOpen = false;
  private filterValue = '';
  protected options: T[] = [];

  constructor(
    @Inject(THEME_TOKEN) private readonly theme: Theme,
    private el: ElementRef,
  ) {}

  private randomId(): string {
    return Math.random().toString(36).substring(2);
  }

  protected get popoverClasses() {
    const popoverStyles = componentStylesByTheme(
      this.theme,
      scdPickerStylesSAG,
      scdPickerStylesSHS,
    );

    return popoverStyles();
  }

  protected get displayValue(): string {
    if (!this.selected) return '';
    return `${this.selected?.firstName} ${this.selected?.lastName}`;
  }

  protected onFocus() {
    if (this.disabled || this.readOnly) return;

    this.isOpen = true;
  }

  protected onBlur() {
    setTimeout(() => {
      this.markAsTouched();
    }, 100);
  }

  protected handleInput(event: Event) {
    const target = event.target as HTMLInputElement;
    this.filterValue = target.value;

    this.searchFn(this.filterValue).then((result) => {
      this.options = result;
    });
  }

  protected handleChange() {
    this.selected = undefined;
    this.writeValue(undefined);
  }

  protected handleSelect(option: T) {
    this.input.nativeElement.focus();
    this.markAsTouched();

    this.selected = option;
    this.writeValue(option);
    this.isOpen = false;

    setTimeout(() => {
      this.input.nativeElement.blur();
    }, 50);
  }

  // eslint-disable-next-line
  onChange = (_value: any) => {};
  // eslint-disable-next-line
  onTouch = () => {};

  // eslint-disable-next-line
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // eslint-disable-next-line
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  writeValue(val: any) {
    this.value = val;
    this.selected = val;
    this.onChange(this.value);
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouch();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  // eslint-disable-next-line
  validate(control: AbstractControl<any, any>): ValidationErrors | null {
    return this.required && !this.selected ? { required: true } : null;
  }

  blockChange(event: Event) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (event.target && (event.target as any)['value']) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (event.target as any)['value'] = this.displayValue;
    }
  }
}
